import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ShortItem } from "@screens/shorts/types/index";
import { TRIP_STATUS } from "utils/index";
import { shortBulkOps } from "services/shortService";

interface ShortState {
  // Cache shorts by a cache key (e.g., concatenated trip IDs)
  shortCache: Record<string, ShortItem[] | null>;
  // Track loading status - single status for all shorts fetching
  status: "idle" | "loading" | "failed";
  // Total count of shorts across all statuses
  totalCount: number;
  // Store shorts organized by approval status for easy filtering
  shortsByStatus: Record<string, ShortItem[]>;
  // Flag to trigger refetch when shorts are created/deleted
  shouldRefetch: boolean;
}

const initialState: ShortState = {
  shortCache: {},
  status: "idle",
  totalCount: 0,
  shortsByStatus: {
    "In-Review": [],
    "Approved": [],
    "Rejected": [],
  },
  shouldRefetch: false,
};

/**
 * Async thunk to fetch shorts for a merchant by status
 * Returns shorts organized by approval status
 * Uses caching to avoid redundant API calls for the same merchant/status
 * @param data - Object containing merchantId and approval status
 */
export const fetchShorts = createAsyncThunk(
  "shorts/fetchShorts",
  async (
    data: { merchantId: string; status?: string; ProjectionExpression?: string[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await shortBulkOps(data.merchantId, data.status || TRIP_STATUS.IN_REVIEW, data.ProjectionExpression);
      
      // Extract reels array from response - API returns array directly or wrapped in data
      let reels: ShortItem[] = response;
      
      const count = reels.length;
      return {
        merchantId: data.merchantId,
        status: data.status,
        cacheKey: `${data.merchantId}_${data.status || "all"}`,
        data: reels,
        count,
      };
    } catch (error) {
      console.error("Error while fetching shorts", error);
      return rejectWithValue(error);
    }
  }
);

const shortSlice = createSlice({
  name: "shorts",
  initialState,
  reducers: {
    /**
     * Clear all shorts cache
     * Called on logout to reset state
     */
    clearShorts: (state) => {
      state.shortCache = {};
      state.status = "idle";
      state.totalCount = 0;
      state.shortsByStatus = {
        "In-Review": [],
        "Approved": [],
        "Rejected": [],
      };
      state.shouldRefetch = false;
    },

    /**
     * Force refresh all shorts cache
     * Used when a new short is created or deleted
     * Sets shouldRefetch flag to true to trigger refetch in component
     */
    forceRefreshShorts: (state) => {
      state.shortCache = {};
      state.shortsByStatus = {
        "In-Review": [],
        "Approved": [],
        "Rejected": [],
      };
      state.shouldRefetch = true;
    },

    /**
     * Reset the refetch flag after refetch is initiated
     */
    resetRefetchFlag: (state) => {
      state.shouldRefetch = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle pending state
      .addCase(fetchShorts.pending, (state) => {
        state.status = "loading";
      })
      // Handle successful fetch
      .addCase(
        fetchShorts.fulfilled,
        (state, action) => {
          state.status = "idle";
          state.totalCount = action.payload.count;
          state.shortCache[action.payload.cacheKey] = action.payload.data;
          state.shouldRefetch = false;

          // Organize shorts by approval status for easy access
          state.shortsByStatus = {
            "In-Review": [],
            "Approved": [],
            "Rejected": [],
          };

          if (action.payload.data) {
            action.payload.data.forEach((short) => {
              const status = short.memoriesStatus;
              if (status in state.shortsByStatus) {
                state.shortsByStatus[status].push(short);
              }
            });
          }
        }
      )
      // Handle fetch error
      .addCase(fetchShorts.rejected, (state, action) => {
        state.status = "failed";
        state.shouldRefetch = false;
        console.error(
          "[memories Error] Failed to fetch memories",
          action.payload
        );
      });
  },
});

export const { clearShorts, forceRefreshShorts, resetRefetchFlag } = shortSlice.actions;
export default shortSlice.reducer;

